import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const warn = (...args) => {
  if (process.env.NODE_ENV !== "production") console.warn(...args);
};

function usePaymentPoll(sessionId, clear) {
  const [phase, setPhase] = useState("checking");
  const [order, setOrder] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setPhase("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const s = await api.paymentStatus(sessionId);
        if (s.payment_status === "paid") {
          try {
            const o = await api.orderBySession(sessionId);
            setOrder(o);
          } catch (err) {
            warn("Order details lookup failed, showing fallback:", err);
            setOrder({ order_number: s.order_number || "CONFIRMED" });
          }
          setPhase("paid");
          clear();
          return;
        }
        if (["failed", "expired"].includes(s.payment_status)) {
          setPhase("error");
          return;
        }
      } catch (err) {
        warn("Payment status check failed, retrying:", err);
      }
      if (tries.current >= 10) {
        setPhase("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return { phase, order };
}

function StatusScreen({ testId, title, titleClass, message, ctaTo, ctaLabel }) {
  return (
    <div className="max-w-2xl mx-auto px-5 pt-28 text-center" data-testid={testId}>
      <p className={`font-college text-3xl ${titleClass}`}>{title}</p>
      <p className="text-sm text-ink/60 mt-4 max-w-md mx-auto">{message}</p>
      {ctaTo && (
        <Link to={ctaTo} className="inline-block mt-8 bg-navy text-paper font-college text-xl px-8 py-3 shadow-print hover:bg-red transition-colors">
          {ctaLabel}
        </Link>
      )}
    </div>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { phase, order } = usePaymentPoll(sessionId, clear);

  if (phase === "checking") {
    return (
      <StatusScreen
        testId="payment-checking"
        title="CONFIRMING YOUR PAYMENT…"
        titleClass="text-navy animate-pulse"
        message="Hold on a second — don't close this page."
      />
    );
  }

  if (phase === "timeout") {
    return (
      <StatusScreen
        testId="payment-timeout"
        title="STILL PROCESSING"
        titleClass="text-navy"
        message="Your payment is taking a moment to confirm. If you were charged, your order is safe — check back shortly or reach out."
        ctaTo="/shop"
        ctaLabel="BACK TO THE SHOP"
      />
    );
  }

  if (phase === "error") {
    return (
      <StatusScreen
        testId="payment-error"
        title="PAYMENT DIDN'T GO THROUGH"
        titleClass="text-red"
        message="Nothing was charged. Your cart is still saved."
        ctaTo="/checkout"
        ctaLabel="TRY AGAIN"
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-5 pt-20 pb-10 text-center" data-testid="order-confirmation">
      <p className="font-college text-lg text-red">ORDER CONFIRMED — PAYMENT RECEIVED</p>
      <h1 className="font-college text-4xl sm:text-5xl text-navy mt-3" data-testid="order-number">{order.order_number}</h1>
      <p className="text-ink/70 mt-5 text-base md:text-lg leading-relaxed">
        {order.customer
          ? `Thanks, ${order.customer.name.split(" ")[0]}. Your order is confirmed and ships to ${order.customer.city}.`
          : "Thanks. Your order is confirmed — a confirmation is on its way to your email."}
      </p>
      {order.items && (
      <div className="mt-8 bg-white text-left shadow-print">
        {order.items.map((i) => (
          <div key={i.variant_id} className="flex justify-between px-5 py-3 border-b border-ink/20 text-sm">
            <span>{i.name} — {i.size} × {i.quantity}</span>
            <span className="font-semibold">${i.price * i.quantity}</span>
          </div>
        ))}
        <div className="flex justify-between px-5 py-3 border-b border-ink/20 text-sm text-ink/60">
          <span>Shipping</span><span>${order.shipping}</span>
        </div>
        <div className="flex justify-between px-5 py-3 font-bold">
          <span>Total</span><span data-testid="order-total">${order.total}</span>
        </div>
      </div>
      )}
      <p className="font-college text-2xl text-red mt-10">KEEP SHOWING UP.</p>
      <Link to="/shop" data-testid="order-back-to-shop" className="inline-block mt-6 bg-navy text-paper font-college text-xl px-8 py-3 shadow-print hover:bg-red transition-colors">
        BACK TO THE SHOP
      </Link>
    </div>
  );
}
